/* eslint no-underscore-dangle: ["error", { "allow": ["_id"]}] */

import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import SortMark from '../Parts/SortMark';
import {
  convertUnixtTimeToDateTime,
  convertUnixtTimeToDateTimeSecond,
} from '../../lib/tools';
import '../../styles/timeline.css';
import MessagePlaceholder from '../Parts/MessagePlaceholder';
import parseTagNames from '../../functions/parseTagNames';
import { getMediaIconSrc } from './functions/getMediaIconSrc';
import { ReactComponent as PlayCircle } from '../../images/common/play_circle.svg';
import { VIDEO } from '../../lib/constants';
import {
  Lists,
  HeaderTableWrapper,
  Table,
  Thead,
  HeaderTr,
  ThColumnCheckbox,
  CheckArea,
  CheckboxWrapper,
  DefaultCheckbox,
  CheckboxLabel,
  ThColumnMedia,
  MediaIcon,
  ThColumnPosition,
  ThColumnDate,
  ThColumnText,
  TdColumnCheckbox,
  TdColumnMedia,
  TdColumnPosition,
  TdColumnDate,
  TdColumnText,
  TableWrapper,
  Tbody,
  SortColumn,
  BodyTr,
  style,
  MediaContainer,
  EllipsisText,
  PlayCircleStyle,
} from './styles/TimeLineListsStyles';

const createHeader = (
  fullscreen,
  noGroupSelected,
  searchOptions,
  checkedAll,
  onCheck,
  onSort,
) => {
  const { sortColumns } = searchOptions;
  const recDateSort = sortColumns.filter(
    sort => sort.column === 'recordedDate',
  );
  const recDateSortOrder = recDateSort.length > 0 ? recDateSort[0].order : '';
  const operatorNameSort = sortColumns.filter(
    sort => sort.column === 'workerName',
  );
  const operatorNameSortOrder =
    operatorNameSort.length > 0 ? operatorNameSort[0].order : '';
  const checkboxValue = 'all';
  return (
    <HeaderTr fullscreen={fullscreen}>
      {fullscreen && (
        <ThColumnCheckbox>
          <CheckArea
            id="timelineCheckAreaHead"
            role="presentation"
            onClick={() => onCheck(checkboxValue, !checkedAll)}
          >
            <CheckboxWrapper
              id="timelineCheckboxAreaHead"
              role="presentation"
              onClick={e => e.stopPropagation()}
            >
              <DefaultCheckbox
                id="timelineCheckboxHeader"
                type="checkbox"
                value={checkboxValue}
                onChange={e => onCheck(e.target.value, e.target.checked)}
                checked={checkedAll ? 'checked' : ''}
              />
              <CheckboxLabel
                className="timelineCheckboxLabel"
                htmlFor="timelineCheckboxHeader"
              />
            </CheckboxWrapper>
          </CheckArea>
        </ThColumnCheckbox>
      )}
      <ThColumnMedia>
        <FormattedMessage id="timeLine.timeLineLists.title.data" />
      </ThColumnMedia>
      <ThColumnPosition>
        <FormattedMessage id="timeLine.timeLineLists.title.position" />
      </ThColumnPosition>
      <ThColumnDate fullscreen={fullscreen}>
        <SortColumn
          center
          marginLeft
          className="clickableItem"
          id="timelineRecDateSortMarkClick"
          role="presentation"
          onClick={() => onSort('recordedDate')}
        >
          <FormattedMessage id="timeLine.timeLineLists.title.recordedDate" />
          <SortMark sort={recDateSortOrder !== ''} order={recDateSortOrder} />
        </SortColumn>
      </ThColumnDate>
      <ThColumnText>
        <SortColumn
          className="clickableItem"
          id="timelineOperatorNameSortMarkClick"
          role="presentation"
          onClick={() => onSort('workerName')}
        >
          <FormattedMessage id="timeLine.timeLineLists.title.operator" />
          <SortMark
            sort={operatorNameSortOrder !== ''}
            order={operatorNameSortOrder}
          />
        </SortColumn>
      </ThColumnText>
      {fullscreen && noGroupSelected && (
        <ThColumnText>
          <FormattedMessage id="timeLine.timeLineLists.title.groupName" />
        </ThColumnText>
      )}
      <th style={style.tagsHeader}>
        <FormattedMessage id="timeLine.timeLineLists.title.tags" />
      </th>
      {fullscreen && (
        <ThColumnText padding="0">
          <FormattedMessage id="timeLine.timeLineLists.title.deviceNickname" />
        </ThColumnText>
      )}
    </HeaderTr>
  );
};

const createList = (
  intl,
  idx,
  fullscreen,
  noGroupSelected,
  list,
  checkbox,
  userGroups,
  onCheck,
  openDetail,
  isCansuke,
) => {
  const { formatMessage } = intl;
  const mediaType = list.mediaType || 0;
  const thumbnailUrl = list.thumbnailUrl || null;
  const foundLatLng = formatMessage({
    id: 'timeLine.timeLineLists.lists.position.yes',
  });
  const notfoundLatLng = formatMessage({
    id: 'timeLine.timeLineLists.lists.position.no',
  });
  const latLng =
    list.lat !== null && list.lng !== null ? foundLatLng : notfoundLatLng;
  const dispName = list.operatorName || list.userId;
  const convertRecordedDate = isCansuke
    ? convertUnixtTimeToDateTimeSecond(list.recordedDate)
    : convertUnixtTimeToDateTime(list.recordedDate);
  const onClickMediaDetail = `onClickMediaDetail${idx}`;
  const lineChecked = checkbox.ids.indexOf(Number(list.id)) > -1;
  const linerClass =
    (lineChecked && 'timelineSelected clickableItem') || 'clickableItem';
  const tagNames = parseTagNames(list.tags);
  const groupNames =
    list.groups &&
    userGroups
      .filter(userGroup => list.groups.find(group => group.id === userGroup.id))
      .map(userGroup => userGroup.name);
  return (
    <BodyTr
      className={linerClass}
      id={onClickMediaDetail}
      key={idx}
      onClick={() => openDetail(list.id)}
    >
      {fullscreen && (
        <TdColumnCheckbox>
          <CheckArea
            id={`timelineCheckArea${idx}`}
            role="presentation"
            onClick={e => {
              e.stopPropagation();
              onCheck(`${list.id}`, !lineChecked);
            }}
          >
            <CheckboxWrapper
              id={`timelineCheckboxArea${idx}`}
              role="presentation"
              onClick={e => e.stopPropagation()}
            >
              <DefaultCheckbox
                id={`timelineCheckbox${idx}`}
                type="checkbox"
                value={list.id}
                onChange={e => onCheck(e.target.value, e.target.checked)}
                checked={lineChecked ? 'checked' : ''}
              />
              <CheckboxLabel
                className="timelineCheckboxLabel"
                htmlFor={`timelineCheckbox${idx}`}
              />
            </CheckboxWrapper>
          </CheckArea>
        </TdColumnCheckbox>
      )}
      <TdColumnMedia>
        <MediaContainer>
          <MediaIcon src={getMediaIconSrc(mediaType, thumbnailUrl)} alt="IMG" />
          {mediaType === VIDEO && thumbnailUrl && (
            <PlayCircle style={PlayCircleStyle} />
          )}
        </MediaContainer>
      </TdColumnMedia>
      <TdColumnPosition>{latLng}</TdColumnPosition>
      <TdColumnDate fullscreen={fullscreen}>{convertRecordedDate}</TdColumnDate>
      <TdColumnText>
        <div style={style.name}>{dispName}</div>
      </TdColumnText>
      {fullscreen && noGroupSelected && (
        <TdColumnText>
          <EllipsisText>{groupNames || ''}</EllipsisText>
        </TdColumnText>
      )}
      <td style={style.tagsBody}>
        {list.freeTag && (
          <div style={style.tagWrapper}>
            <font>{list.freeTag}</font>
          </div>
        )}
        <div style={style.tagWrapper}>
          {tagNames.map((levelTags, levelIndex) =>
            levelTags.map((tag, tagIndex) => {
              const key = `tag-${levelIndex}-${tagIndex}`;
              return (
                <React.Fragment key={key}>
                  {tag}
                  {tagIndex < levelTags.length - 1 ? ' > ' : ''}
                  {tagIndex === levelTags.length - 1 &&
                  levelIndex < tagNames.length - 1
                    ? ', '
                    : ''}
                </React.Fragment>
              );
            }),
          )}
        </div>
      </td>
      {fullscreen && (
        <TdColumnText>
          <EllipsisText>{list.deviceNickname}</EllipsisText>
        </TdColumnText>
      )}
    </BodyTr>
  );
};

const TimeLineLists = ({
  fullscreen,
  noGroupSelected, // 企業管理者の場合はグループ選択時はグループ名を表示しない
  timelines,
  searchOptions,
  checkbox,
  userGroups,
  openDetail,
  onCheck,
  onSort,
  provider,
}) => {
  const intl = useIntl();
  const checkedAll =
    (timelines.length > 0 &&
      timelines.length ===
        timelines.filter(x => checkbox.ids.indexOf(x.id) > -1).length) ||
    false;
  const isCansuke = provider && provider.isCansuke;
  return (
    <Lists className="timeLineListsts">
      <HeaderTableWrapper>
        <Table>
          <Thead>
            {createHeader(
              fullscreen,
              noGroupSelected,
              searchOptions,
              checkedAll,
              onCheck,
              onSort,
            )}
          </Thead>
        </Table>
      </HeaderTableWrapper>
      <TableWrapper id="canScrollTable">
        <Table>
          {noGroupSelected ? (
            <tbody>
              <tr>
                <td>
                  <MessagePlaceholder messageId="timeLine.selectGroup" />
                </td>
              </tr>
            </tbody>
          ) : (
            (timelines && timelines.length > 0 && (
              <Tbody>
                {timelines.map((list, idx) =>
                  createList(
                    intl,
                    idx,
                    fullscreen,
                    noGroupSelected,
                    list,
                    checkbox,
                    userGroups,
                    onCheck,
                    openDetail,
                    isCansuke,
                  ),
                )}
              </Tbody>
            )) || (
              <tbody>
                <tr>
                  <td>
                    <MessagePlaceholder />
                  </td>
                </tr>
              </tbody>
            )
          )}
        </Table>
      </TableWrapper>
    </Lists>
  );
};

TimeLineLists.propTypes = {
  fullscreen: PropTypes.bool.isRequired,
  noGroupSelected: PropTypes.bool.isRequired,
  timelines: PropTypes.array.isRequired,
  searchOptions: PropTypes.object.isRequired,
  checkbox: PropTypes.object.isRequired,
  userGroups: PropTypes.array.isRequired,
  openDetail: PropTypes.func.isRequired,
  onCheck: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  provider: PropTypes.object.isRequired,
};

export default TimeLineLists;
